export interface IndustryItem {
  id: string;
  label: string;
  subtypes: string[];
}

export interface IndustryGroup {
  group: string;
  items: IndustryItem[];
}

function toId(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export const INDUSTRIES: IndustryGroup[] = [
  {
    group: "Primary Industries",
    items: [
      { id: "agriculture", label: "Agriculture", subtypes: ["Cropping / horticulture", "Livestock & dairy", "Mixed farming"] },
      { id: "forestry", label: "Forestry", subtypes: ["Plantation forestry", "Native / natural forest", "Carbon forestry", "Nursery & seedlings", "Harvesting & logging", "Forest management"] },
      { id: "fishing_seafood", label: "Fishing & Seafood", subtypes: ["Wild catch / commercial fishing", "Finfish aquaculture", "Shellfish aquaculture", "Seaweed / algae", "Seafood processing", "Seafood wholesale & distribution"] },
      { id: "kiwifruit_horticulture", label: "Kiwifruit & Horticulture", subtypes: ["Kiwifruit orchards", "Pip/stonefruit", "Avocado & subtropical", "Commercial vegetables", "Post-harvest & packing", "Plant nursery & propagation"] },
      { id: "dairy_processing", label: "Dairy Processing", subtypes: ["Ingredients / commodities", "Consumer dairy", "Specialty / nutritional", "Contract processing", "Dairy farming (co-op context)"] },
      { id: "mining_resources", label: "Mining & Resources", subtypes: ["Hard rock mining", "Oil & gas", "Quarrying / aggregates"] },
      { id: "timber_wood", label: "Timber & Wood Products", subtypes: ["Sawmilling", "Engineered wood", "Joinery & cabinetry", "Furniture manufacturing", "Pallets & packaging", "Timber treatment & preserving", "Panel & board products"] },
      { id: "wine_viticulture", label: "Wine & Viticulture", subtypes: ["Estate / vineyard owner", "Winery / producer", "Cellar door & hospitality", "Wine export / distribution", "Contract grape growing"] },
      { id: "grain_bulk", label: "Grain & Bulk Commodities (AU)", subtypes: ["Grain handling & storage", "Grain marketing & trading", "Cotton ginning & marketing"] },
      { id: "meat_processing", label: "Meat Processing (NZ/AU)", subtypes: ["Red meat processing", "Poultry processing", "Venison / game", "Rendering & pet food", "Hides & skins"] },
    ],
  },
  {
    group: "Technology & SaaS",
    items: [
      { id: "saas", label: "SaaS / Technology", subtypes: ["Pure SaaS", "Marketplace / platform", "Hybrid (software + services)", "Hardware + software"] },
      { id: "data_centres", label: "Data Centres", subtypes: ["Colocation", "Hyperscale", "Edge / micro", "Managed hosting"] },
      { id: "telecom", label: "Telecommunications", subtypes: ["Mobile network operator", "MVNO / reseller", "ISP / broadband", "Tower / infrastructure"] },
    ],
  },
  {
    group: "Construction & Real Estate",
    items: [
      { id: "construction", label: "Construction", subtypes: ["Building & civil", "Fitout & refurbishment", "Residential development", "Heavy infrastructure"] },
      { id: "real_estate", label: "Real Estate", subtypes: ["Commercial investment", "Residential development", "Industrial / logistics", "Property funds / REIT"] },
      { id: "self_storage", label: "Self-Storage", subtypes: ["Traditional self-storage", "Portable / mobile storage", "Business / records storage"] },
      { id: "flexible_workspace", label: "Flexible Workspace / Coworking", subtypes: ["Coworking", "Serviced offices", "Hybrid / enterprise"] },
    ],
  },
  {
    group: "Healthcare",
    items: [
      { id: "healthcare", label: "Healthcare", subtypes: ["Primary care / GP", "Specialist / hospital", "Aged care / disability", "Allied health / dental"] },
      { id: "aged_care", label: "Aged Care & Retirement Living", subtypes: ["Residential aged care", "Home care", "Retirement villages"] },
      { id: "pharma", label: "Pharma & Life Sciences", subtypes: ["Pharmaceutical manufacturing", "Biotech / R&D", "Medical devices", "Contract research (CRO)", "Pharma distribution"] },
      { id: "veterinary", label: "Veterinary Services", subtypes: ["Companion animal", "Equine", "Large animal / rural"] },
      { id: "ndis_disability", label: "NDIS & Disability Services (AU)", subtypes: ["NDIS registered provider", "Supported independent living", "Day programmes & community", "Allied health / therapy", "Support coordination / plan mgmt"] },
    ],
  },
  {
    group: "Consumer & Retail",
    items: [
      { id: "retail", label: "Retail", subtypes: ["Brick & mortar", "E-commerce", "Omnichannel", "Franchise"] },
      { id: "hospitality", label: "Hospitality & Tourism", subtypes: ["Hotels / accommodation", "Restaurants / F&B", "Tourism operators", "Events & conferencing"] },
      { id: "fitness", label: "Fitness & Wellness", subtypes: ["Gyms / health clubs", "Spa / beauty / wellness", "Franchise fitness"] },
      { id: "cpg", label: "Consumer Packaged Goods (CPG)", subtypes: ["Food & beverage CPG", "Personal care & beauty", "Household products", "Pet food & products"] },
      { id: "fashion", label: "Fashion & Apparel", subtypes: ["Brand / designer", "Apparel manufacturing", "Wholesale / distribution", "Fashion retail"] },
      { id: "craft_beverages", label: "Craft Beverages", subtypes: ["Craft brewery", "Distillery", "Cider / RTD", "Non-alcoholic / specialty"] },
      { id: "pet_services", label: "Pet Services", subtypes: ["Grooming", "Boarding / kennels", "Daycare / walking", "Training"] },
      { id: "pubs_bars_uk", label: "Pubs & Bars (UK)", subtypes: ["Freehold / free house", "Tenanted / leased", "Managed house", "Pub group / pubco"] },
    ],
  },
  {
    group: "Manufacturing",
    items: [
      { id: "manufacturing", label: "Manufacturing", subtypes: ["Food & beverage processing", "Metals & steel", "Chemicals & plastics", "Building products", "Electronics & electrical", "Machinery & equipment", "Automotive components", "Textiles & apparel manufacturing", "Paper & pulp", "Glass & ceramics", "Contract / toll manufacturing", "Medical device manufacturing", "Heavy / capital equipment"] },
      { id: "printing_packaging", label: "Printing & Packaging", subtypes: ["Commercial printing", "Packaging manufacturing", "Signage & display"] },
    ],
  },
  {
    group: "Financial Services",
    items: [
      { id: "financial", label: "Financial Services", subtypes: ["Lending / banking", "Insurance", "Wealth management", "Fintech"] },
      { id: "insurance_broking", label: "Insurance Broking", subtypes: ["General insurance broking", "Life / health broking", "Underwriting agencies"] },
      { id: "superannuation", label: "Superannuation & Pension Funds", subtypes: ["Industry fund", "Retail fund", "Corporate fund", "SMSF administration", "Pension fund (Canada/NZ)"] },
    ],
  },
  {
    group: "Professional & Business Services",
    items: [
      { id: "prof_services", label: "Professional Services", subtypes: ["Consulting / advisory", "Legal", "Accounting / audit", "Engineering / architecture"] },
      { id: "recruitment", label: "Recruitment & Staffing", subtypes: ["Temporary / contract staffing", "Permanent placement", "RPO / managed services"] },
      { id: "security_services", label: "Security Services", subtypes: ["Manned guarding", "Electronic / tech security", "Cybersecurity", "Cash-in-transit"] },
      { id: "cleaning", label: "Cleaning & Facility Services", subtypes: ["Commercial cleaning", "Specialist cleaning", "Integrated facility management"] },
      { id: "lab_testing", label: "Laboratory & Testing Services", subtypes: ["Environmental testing", "Food safety testing", "Medical / pathology", "Materials / industrial", "Forensic / specialist"] },
      { id: "industrial_services", label: "Industrial Services", subtypes: ["Shutdown / turnaround", "Scaffolding & access", "Insulation & fireproofing", "Industrial maintenance"] },
      { id: "trades", label: "Trades & Home Services", subtypes: ["Plumbing", "Electrical", "HVAC / refrigeration", "Pest control", "Landscaping / grounds", "General trades / handyman"] },
    ],
  },
  {
    group: "Transport & Logistics",
    items: [
      { id: "transport", label: "Transport & Logistics", subtypes: ["Freight / haulage", "Warehousing / 3PL", "Last-mile delivery", "Passenger transport"] },
      { id: "aviation", label: "Aviation", subtypes: ["Airlines", "Airports", "MRO / maintenance", "Charter / private aviation", "Ground handling"] },
      { id: "marine", label: "Marine & Shipping", subtypes: ["Shipping lines", "Ports & terminals", "Marine services", "Ferries"] },
      { id: "automotive", label: "Automotive", subtypes: ["Dealerships", "Parts & aftermarket", "Service & repair", "Rental & leasing"] },
    ],
  },
  {
    group: "Energy & Resources",
    items: [
      { id: "energy", label: "Energy & Utilities", subtypes: ["Power generation", "Renewables", "Energy retail", "Water / wastewater"] },
      { id: "oil_gas", label: "Oil & Gas", subtypes: ["Upstream / E&P", "Oil sands / heavy oil", "Midstream / pipelines", "Oilfield services", "LNG"] },
      { id: "mining_services", label: "Mining Services", subtypes: ["Contract mining", "Drilling", "Blasting & explosives", "Crushing & screening", "Mining equipment & parts", "Mining technology / METS"] },
      { id: "waste", label: "Waste & Environmental Services", subtypes: ["Collection / hauling", "Landfill / disposal", "Recycling / resource recovery", "Industrial / hazardous"] },
    ],
  },
  {
    group: "Education & Media",
    items: [
      { id: "education", label: "Education & Training", subtypes: ["K-12 / Schools", "Higher education", "Vocational / RTO", "Early childhood / Childcare", "EdTech"] },
      { id: "childcare", label: "Childcare (AU/NZ)", subtypes: ["Long day care", "Outside school hours care", "Family day care", "Kindergarten / sessional"] },
      { id: "media", label: "Media & Entertainment", subtypes: ["Publishing", "Broadcasting / streaming", "Gaming", "Advertising / agency", "Live entertainment"] },
    ],
  },
  {
    group: "Defence & Aerospace",
    items: [
      { id: "defence", label: "Defence & Aerospace", subtypes: ["Defence manufacturing", "Defence services", "Aerospace / space", "Defence cyber / intelligence"] },
      { id: "aerospace", label: "Aerospace", subtypes: ["Aircraft MRO", "Aerostructures & components", "Engine & propulsion", "Avionics & systems", "Space & satellite", "UAV / drones", "Aircraft interiors"] },
    ],
  },
  {
    group: "Infrastructure",
    items: [
      { id: "infrastructure_ppp", label: "Infrastructure / PPP", subtypes: ["PPP / concession", "Toll roads", "Regulated utilities (infra)", "Social infrastructure"] },
    ],
  },
  {
    group: "Sports & Gaming",
    items: [
      { id: "pro_sports", label: "Professional Sports & Clubs", subtypes: ["Football (soccer)", "Rugby", "Cricket", "AFL / Aussie Rules", "Other team sports", "Motorsport", "Individual sport / academy"] },
      { id: "gambling", label: "Gambling & Betting", subtypes: ["Casinos", "Online betting / bookmakers", "Lotteries", "Gaming machines / pubs & clubs", "iGaming"] },
      { id: "racing", label: "Racing Industry", subtypes: ["Thoroughbred racing", "Harness racing", "Greyhound racing", "Breeding & stud", "Training & spelling"] },
      { id: "indigenous_gaming_ca", label: "Indigenous Gaming (Canada)", subtypes: ["First Nations casino", "Gaming commission", "Revenue sharing"] },
    ],
  },
  {
    group: "Social & Community",
    items: [
      { id: "nfp", label: "Not-for-Profit / Social Enterprise", subtypes: ["Charities / NGOs", "Social enterprise", "Government-contracted services"] },
      { id: "funeral", label: "Funeral & Cemetery Services", subtypes: ["Funeral directors", "Cemeteries & crematoria", "Pre-need / insurance"] },
      { id: "social_housing_uk", label: "Social Housing (UK)", subtypes: ["Housing association / RP", "Local authority housing", "Affordable housing developer", "Housing management"] },
      { id: "nhs_uk", label: "NHS & Health Services (UK)", subtypes: ["Acute / hospital trust", "Primary care / GP", "Community health", "Mental health", "NHS outsourced / independent"] },
      { id: "care_homes_uk", label: "Care Homes (UK)", subtypes: ["Residential care", "Nursing care", "Specialist care", "Domiciliary / home care"] },
    ],
  },
  {
    group: "Indigenous Enterprise",
    items: [
      { id: "iwi_maori", label: "Iwi / Māori Enterprise", subtypes: ["Post-settlement governance entity", "Māori incorporation", "Māori land trust / Ahu Whenua", "Iwi commercial entity", "Māori social / cultural entity"] },
      { id: "aboriginal_au", label: "Aboriginal & Torres Strait Islander Enterprise", subtypes: ["Prescribed body corporate (PBC)", "Aboriginal land council", "Indigenous commercial entity", "Aboriginal art & culture", "Community development"] },
      { id: "first_nations_ca", label: "First Nations Enterprise (Canada)", subtypes: ["Band / Nation government", "Development corporation", "Settlement trust / trust fund", "Land management / resources", "Social enterprise / housing"] },
    ],
  },
  {
    group: "Other",
    items: [
      { id: "cannabis", label: "Cannabis", subtypes: ["Licensed producer / cultivator", "Processing & extraction", "Cannabis retail", "Medical cannabis", "Industrial hemp"] },
    ],
  },
];
